import React, { forwardRef, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { TextField as MuiTextField, InputAdornment, useTheme, useMediaQuery, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { NumberField as ShellNumberField } from '@e3dc-react/shell/Elements/InputFields/NumberField'
import { LocalProps } from '../../formik/numberField/NumberField'
import { useTranslation } from 'react-i18next'
import { isAboveMaxLimit, isBelowMinLimit, isNumericInputAllowed, parseDecimalSeparator } from '../../formik/numberField/NumberFieldUtils'
import { useSuffixFieldStyles } from '../../hooks/useSuffixFieldStyles'

interface Props extends LocalProps {
  propsOnChange?: (newValue: number | string) => void
}

const useStyles = makeStyles()(theme => ({
  input: {
    '& .MuiInputBase-root': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  suffix: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 34,
    marginLeft: theme.spacing(2),
    paddingInline: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.shades.shade02}`,
    '& > span': {
      minWidth: 48,
      textAlign: 'center',
    },
  },
  emptyAdornment: {
    paddingRight: theme.spacing(2),
  },
}))

export const DecimalField = forwardRef<typeof ShellNumberField, Props>((props, ref) => {
  const {
    name,
    textAlign = 'right',
    fullWidth,
    prefix,
    suffix,
    isDecimal = true,
    step,
    precision,
    ignoreNonNumerics,
    ignoreDefaultValue,
    min,
    max,
    propsOnChange,
    ...otherProps
  } = props
  const { classes, cx } = useStyles()
  const { classes: suffixFieldClasses } = useSuffixFieldStyles()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const applyFullWidth = fullWidth || downSm
  const { setValue, watch, getFieldState } = useFormContext()
  const fieldValue = watch(name)
  const meta = getFieldState(name)
  const { i18n } = useTranslation()

  const appLanguage = i18n.language
  const defaultValue = ignoreDefaultValue ? '' : '0'
  const displayValue = fieldValue ? parseDecimalSeparator(fieldValue.toString(), appLanguage) : defaultValue

  const hasError = useMemo<boolean>(
    () => {
      if (otherProps.disabled) return false
      return Boolean(meta.error && (meta.isDirty || meta.isTouched))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.name, meta]
  )

  const handleChangeEvent = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      propsOnChange?.(value)
      if (name) {
        setValue(name, value.replace(',', '.'), { shouldValidate: true, shouldDirty: true })
      } else {
        console.warn('Invalid fieldName', name)
      }
    },
    [name, propsOnChange, setValue]
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      /* if the field is decimal and the user enters integer number e.g: 2, this event will set it to 2.00 */
      if (isDecimal && precision) {
        const originalValue = event.target.value
        if (originalValue === '') return

        const formattedValue = parseFloat(originalValue.replace(',', '.')).toFixed(precision)

        setValue(name, formattedValue, { shouldValidate: true })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, isDecimal]
  )

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    if (downSm) event.target.select()
  }

  const formatValue = useCallback(
    (value: number): string => {
      if (isDecimal && precision) {
        return value.toFixed(precision)
      }

      return value.toString()
    },
    [isDecimal, precision]
  )

  const handleArrowKeys = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

      const currentValue = parseFloat(fieldValue) || 0
      const stepValue = step ?? 1
      let newValue = currentValue

      if (event.key === 'ArrowUp') {
        newValue = currentValue + stepValue
      } else if (event.key === 'ArrowDown') {
        newValue = currentValue - stepValue
      }

      if (isBelowMinLimit(newValue, min) || isAboveMaxLimit(newValue, max)) {
        return
      }

      setValue(name, formatValue(newValue), { shouldValidate: true })
    },
    [step, min, max, setValue, name, fieldValue, formatValue]
  )

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement
      const value = input.value
      const keyValue = event.key

      if (!isNumericInputAllowed(keyValue, value, isDecimal ?? false)) {
        event.preventDefault()
      }
    },
    [isDecimal]
  )

  return (
    <MuiTextField
      {...otherProps}
      className={cx(classes.input, suffix && suffixFieldClasses.suffixField)}
      fullWidth={applyFullWidth}
      type="text"
      InputProps={{
        endAdornment: suffix ? (
          <Box className={classes.suffix}>
            <Box component="span">{suffix}</Box>
          </Box>
        ) : (
          <InputAdornment position="end" className={classes.emptyAdornment} />
        ),
        startAdornment: prefix && (
          <InputAdornment position="start" sx={{ position: 'absolute', ml: 1, top: '50%', transform: 'translateY(-50%)' }}>
            {<Box>{prefix}</Box>}
          </InputAdornment>
        ),
        inputProps: {
          style: { textAlign },
        },
      }}
      value={displayValue || ''}
      onChange={handleChangeEvent}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleArrowKeys}
      onKeyPress={handleKeyPress}
      error={hasError}
      helperText={hasError ? meta.error?.message : undefined}
      inputRef={ref}
    />
  )
})

DecimalField.displayName = 'DecimalField'
